import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { switchMap, take, filter } from 'rxjs/operators';
import { IResponseSuccess } from '../../interfaces';
import { Store as StoreModel, OrderType } from '../../models';
import { Store } from '@ngrx/store';
import { AppState } from '@store/reducers';
import { selectStore } from '@store/selectors';
import { isHourInSchedule } from '../../helpers/general.helper';

@Injectable({
	providedIn: 'root',
})
export class OrderTypeService {
	store$: Observable<StoreModel | null>;

	constructor(
		private http: HttpClient,
		private store: Store<AppState>
	) {
		this.store$ = this.store.select(selectStore);
	}

	find(localChannelId: string | null): Observable<IResponseSuccess<OrderType>> {
		return this.store$.pipe(
			filter((store): store is StoreModel => !!store),
			filter(() => !!localChannelId),
			switchMap(store => {
				return this.http.get<IResponseSuccess<OrderType>>(`${environment.microConfiguraciones}/tiposorden`, {
					params: {
						localId: store.idLocal,
						localCanalId: localChannelId as string,
					},
					headers: {
						authorization: store.apiKey,
					},
				});
			}),
			take(1)
		);
	}

	/**
	 * Verifica si el horario actual está dentro del rango de DELIVERY o PICK-UP
	 * @param orderTypes lista de tipos de orden
	 * @returns { delivery: boolean, pickup: boolean }
	 */
	public checkOrderTypeAvailabilityByCurrentTime(orderTypes: OrderType[]): { delivery: boolean; pickup: boolean } {
		const now = new Date();
		const today = now.toLocaleString('en-US', { weekday: 'long' }).toLowerCase();
		const currentTime = now.getHours() * 100 + now.getMinutes();

		// DELIVERY
		const deliveryType = orderTypes.find(ot => ot.codigo.toLowerCase() === 'delivery');
		let delivery = false;
		if (deliveryType && deliveryType.canales?.length) {
			const canal = deliveryType.canales.find(c => c.configuracion && c.configuracion.diasAsociados);
			if (canal && canal.configuracion && canal.configuracion.diasAsociados) {
				const dias = canal.configuracion.diasAsociados.filter(
					d => (d.nombreDia || '').toLowerCase() === today && d.habilitado
				);
				// isHourInSchedule espera un array de días y la hora actual
				delivery = dias.length > 0 && isHourInSchedule(dias, currentTime);
			}
		}

		// PICK-UP
		const pickupType = orderTypes.find(ot => ot.codigo.toLowerCase() === 'pick-up');
		let pickup = false;
		if (pickupType && pickupType.canales?.length) {
			const canal = pickupType.canales.find(c => c.configuracion && c.configuracion.diasAsociados);
			if (canal && canal.configuracion && canal.configuracion.diasAsociados) {
				const dias = canal.configuracion.diasAsociados.filter(
					d => (d.nombreDia || '').toLowerCase() === today && d.habilitado
				);
				pickup = dias.length > 0 && isHourInSchedule(dias, currentTime);
			}
		}

		return { delivery, pickup };
	}

	public checkOrderTypeAvailabilityByOrderType(orderTypes: OrderType[]): { delivery: boolean; pickup: boolean } {
		// DELIVERY
		const deliveryType = orderTypes.find(ot => ot.codigo.toLowerCase() === 'delivery');
		let delivery = !!deliveryType?.canales.find(c => c.configuracion && c.configuracion.diasAsociados && c.habilitado);

		// PICK-UP
		const pickupType = orderTypes.find(ot => ot.codigo.toLowerCase() === 'pick-up');
		let pickup = !!pickupType?.canales.find(c => c.configuracion && c.configuracion.diasAsociados && c.habilitado);

		return { delivery, pickup };	
	}
}
